import { Mesh, Object3D, Material, Color, ColorRepresentation } from "three"
import { Group as TweenGroup } from "./vendor/tweenjs/tween.esm"

/**
 * A collection of THREE objects comprising a globe region.
 *
 * The region has:
 * * A face mesh
 * * A top border mesh
 * * A bottom border mesh
 * * An anchor object where its label should go
 */
export class Region {
    #topBorder
    #bottomBorder
    #face
    #anchor
    #label
    #visible
    #disposed

    /**
     *
     * @param {Mesh} mesh
     * @param {RegionDescriptor[]} regionDescriptorQueue - LIFO queue
     */
    constructor(mesh) {
        const { face, topBorder, bottomBorder, anchor } = this._parseObj(mesh)
        this.#face = face
        this.#topBorder = topBorder
        this.#bottomBorder = bottomBorder
        this.#anchor = anchor
        this.#visible = true
        this.#disposed = false
    }

    /**
     * Clean up resources associated with this region and remove it from the scene graph
     * @param {Boolean} disposeMaterials
     * @param {Boolean} disposeTextures
     */
    dispose(disposeMaterials, disposeTextures) {
        this.#disposed = true
        this.face.geometry.dispose()
        this.topBorder.geometry.dispose()
        this.bottomBorder.geometry.dispose()

        if (disposeTextures) {
            this.disposeTextures()
        }

        if (disposeMaterials) {
            this.disposeMaterials()
        }

        this.face.parent?.remove(this.face)
    }

    disposeTextures() {
        const materials = [
            this.face.material,
            this.topBorder.material,
            this.bottomBorder.material,
        ]
        materials.forEach((material) => {
            for (const key of Object.keys(material)) {
                const value = material[key]
                if (
                    value &&
                    typeof value === "object" &&
                    "minFilter" in value
                ) {
                    value.dispose()
                }
            }
        })
    }

    disposeMaterials() {
        this.face.material.dispose()
        this.topBorder.material.dispose()
        this.bottomBorder.material.dispose()
    }

    highlight(color = "#FFF") {}
    unhighlight() {}

    raise() {}
    lower() {}
    hide() {
        this.#visible = false
        this.face.visible = false
        this.topBorder.visible = false
        this.bottomBorder.visible = false
    }

    show() {
        this.#visible = true
        this.face.visible = true
        this.topBorder.visible = true
        this.bottomBorder.visible = true
    }

    get isRegion() {
        return true
    }

    get face() {
        return this.#face
    }

    get topBorder() {
        return this.#topBorder
    }

    get bottomBorder() {
        return this.#bottomBorder
    }

    get anchor() {
        return this.#anchor
    }

    get label() {
        return this.#anchor.children.find(child => child.userData.labelType === "name")
    }

    get visible() {
        return this.#visible
    }

    get disposed() {
        return this.#disposed
    }

    /**
     * Separate a mesh into its components
     * @param {Mesh} obj
     * @returns {{face: Mesh, topBorder: Mesh, bottomBorder: Mesh, anchor: Object3D}}
     * @example const {face, topBorder, bottomBorder, anchor} = parseObj(mesh)
     */
    _parseObj(obj) {
        if (!obj.name.includes("_")) {
            return Object.assign(
                { face: obj },

                // Recursively parse each child mesh
                ...obj.children.map((obj) => this._parseObj(obj))
            )
        }

        const lastWord = obj.name.split("_").at(-1)
        if (lastWord.startsWith("TopBorder")) {
            return { topBorder: obj }
        }
        if (lastWord.startsWith("BottomBorder")) {
            return { bottomBorder: obj }
        }
        if (lastWord.startsWith("Anchor")) {
            return { anchor: obj }
        }
        return Object.assign(
            { face: obj },

            // Recursively parse each child mesh
            ...obj.children.map((obj) => this._parseObj(obj))
        )
    }

    get id() {
        return this.#face.name
    }

    /**
     * Get the Region associated with a particular mesh
     *
     * @param {Mesh} obj
     * @returns {Region}
     * @throws
     */
    static lookup(mesh) {
        if ("region" in mesh && mesh.region instanceof Region) {
            return mesh.region
        } else {
            throw new Error("Mesh did not have a region property", mesh)
        }
    }
}

export class HighlightableFaceRegion extends Region{
    /**
     * 
     * @param {ColorRepresentation} color 
     */
    highlight(color = "#FFF") {
        /**
         * @type Color
         */
        const colorObject = this.face.material.color
        colorObject.set(color)
    }

    unhighlight() {
        /**
         * @type Color
         */
        const colorObject = this.face.material.color
        colorObject.copy(this.face.userData.inactiveColor)
    }
}

export class AnimatedRegion extends Region {}

export class MapRegion extends Region {
    #materials
    #mapLevel
    #inactive
    #holePunch

    /**
     *
     * @param {Mesh} mesh
     * @param {{active: Material[], inactive: Material[], holePunch: Material[]}} materials
     */
    constructor(mesh, materials) {
        super(mesh)
        this.#materials = materials
        this.#mapLevel = 0
        this.#inactive = false
        this.#holePunch = false
    }

    highlight() {
        if (this.#inactive && !this.#holePunch) {
            this.face.material = this.#materials.active[this.#mapLevel]
        }
    }

    unhighlight() {
        if (this.#inactive && !this.#holePunch) {
            this.face.material = this.#materials.inactive[this.#mapLevel]
        }
    }

    deactivate() {
        this.#inactive = true
        this._setMapMaterial()
    }

    activate() {
        this.#inactive = false
        this._setMapMaterial()
    }

    setMapLevel(level) {
        this.#mapLevel = level
        this._setMapMaterial(level)
    }

    /**
     * Set the material to the hole punch material
     *
     * Allows the vector map to be seen under the globe canvas
     */
    holePunch() {
        this.#holePunch = true
        this._setMapMaterial()
    }

    unHolePunch() {
        this.#holePunch = false
        this._setMapMaterial()
    }

    get isMapRegion() {
        return true
    }

    get isHolePunched() {
        return this.#holePunch
    }

    _setMapMaterial() {
        let materials
        if (this.#holePunch) {
            materials = this.#materials.holePunch
        } else {
            materials = this.#inactive
                ? this.#materials.inactive
                : this.#materials.active
        }

        this.face.material = materials[this.#mapLevel]
    }
}

export class SuperRegion {
    #topLevel
    #topBorder
    /**
     *
     * @param {Object3D} topLevelObject
     */
    constructor(topLevel) {
        this.#topLevel = topLevel
        this.regions = []
        this.#parseObj(topLevel)
    }

    get topBorder() {
        return this.#topBorder
    }

    // TODO OPTIMIZATION-MEMORY: Don't construct a new list every time
    get faces() {
        return this.regions.map((region) => region.face)
    }

    // TODO OPTIMIZATION-MEMORY: Don't construct a new list every time
    get topBorders() {
        return this.regions.map((region) => region.topBorder)
    }

    #parseObj(obj) {
        if (!obj.children) {
            console.error("Object has no children", obj)
            return
        }
        obj.children.forEach((child) => {
            const lastWord = child.name.split("_").at(-1)
            if (lastWord.startsWith("TopBorder")) {
                this.#topBorder = child
                return
            }
            this.regions.push(new Region(child))
        })
    }
}
